import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import {
  pageSectionHeaderSx,
  pageSectionSubtitleSx,
  pageSectionTitleSx,
} from "../constants/pageSectionStyles";
import {
  regulationDetailsSectionTabStripSx,
  regulationDetailsSectionTabsSx,
} from "../constants/regulationDetailsNavTabStyles";
import { useSnackbar } from "../contexts/SnackbarContext";
import { useGuardedAsyncCallback } from "../hooks/useGuardedAsyncCallback";
import { useAcTenantId } from "../hooks/useAcTenantId";
import { useHasPrivilege, useHasPrivilegeStatus } from "../hooks/useHasPrivilege";
import {
  fetchAutoTaggingEnabled,
  updateAutoTaggingEnabled,
} from "../services/autoTaggingService";
import { AUTO_TAGGING_PRIVILEGES } from "../services/permissionsService";
import TaggingPlanTab from "./AiPlanPage/TaggingPlan/TaggingPlanTab";
import EnhancedTaggingRuleTab from "./AiPlanPage/EnhancedTaggingRule/EnhancedTaggingRuleTab";
import AutoTaggingConfirmDialog, {
  type AutoTaggingConfirmMode,
} from "./AiPlanPage/AutoTaggingConfirmDialog";
import {
  AI_PLAN_TAB_LABELS,
  AI_PLAN_TAB_QUERY_KEY,
  isAiPlanTabKey,
  type AiPlanTabKey,
} from "./AiPlanPage/constants";
import { useRegulationsNextShellIntegration } from "./shared/useRegulationsNextShellIntegration";

export default function AiPlanPage() {
  useRegulationsNextShellIntegration();
  const { showMessage } = useSnackbar();
  const acTenantId = useAcTenantId();
  const { has: canReadAutoTagging, loaded: privilegesLoaded } =
    useHasPrivilegeStatus(AUTO_TAGGING_PRIVILEGES.read);
  const canExecuteAutoTagging = useHasPrivilege(AUTO_TAGGING_PRIVILEGES.execute);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get(AI_PLAN_TAB_QUERY_KEY);
  // URL is the source of truth — avoids a race where setTab + setSearchParams
  // briefly leaves tabFromUrl on the previous tab and the sync effect reverts.
  const tab: AiPlanTabKey = isAiPlanTabKey(tabFromUrl)
    ? tabFromUrl
    : "taggingPlan";
  const [autoTaggingEnabled, setAutoTaggingEnabled] = useState(false);
  const [autoTaggingStatusLoading, setAutoTaggingStatusLoading] = useState(true);
  const [autoTaggingConfirmMode, setAutoTaggingConfirmMode] =
    useState<AutoTaggingConfirmMode | null>(null);
  const [savingAutoTagging, setSavingAutoTagging] = useState(false);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, value: AiPlanTabKey) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === "taggingPlan") {
            next.delete(AI_PLAN_TAB_QUERY_KEY);
          } else {
            next.set(AI_PLAN_TAB_QUERY_KEY, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (!canReadAutoTagging) {
      setAutoTaggingStatusLoading(false);
      return;
    }

    let cancelled = false;

    setAutoTaggingStatusLoading(true);
    fetchAutoTaggingEnabled()
      .then((enabled) => {
        if (!cancelled) {
          setAutoTaggingEnabled(enabled);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAutoTaggingStatusLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [acTenantId, canReadAutoTagging]);

  const handleAutoTaggingSwitchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!canExecuteAutoTagging) return;
      setAutoTaggingConfirmMode(event.target.checked ? "turnOn" : "turnOff");
    },
    [canExecuteAutoTagging],
  );

  const handleCloseAutoTaggingConfirm = useCallback(() => {
    if (savingAutoTagging) return;
    setAutoTaggingConfirmMode(null);
  }, [savingAutoTagging]);

  const handleConfirmAutoTagging = useGuardedAsyncCallback(async () => {
    if (!autoTaggingConfirmMode || savingAutoTagging || !canExecuteAutoTagging) {
      return;
    }

    const nextEnabled = autoTaggingConfirmMode === "turnOn";
    setSavingAutoTagging(true);
    try {
      const enabled = await updateAutoTaggingEnabled(nextEnabled);
      setAutoTaggingEnabled(enabled);
      setAutoTaggingConfirmMode(null);
      showMessage(
        enabled ? "Auto-tagging turned on." : "Auto-tagging turned off.",
        "success",
      );
    } catch (error) {
      showMessage(
        error instanceof Error
          ? error.message
          : "Failed to update auto-tagging.",
        "error",
      );
    } finally {
      setSavingAutoTagging(false);
    }
  });

  const showAccessDenied = privilegesLoaded && !canReadAutoTagging;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ ...pageSectionHeaderSx, pb: 0, pl: 0, pr: "24px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                mb: "4px",
              }}
            >
              <Typography sx={pageSectionTitleSx}>
                Organisational AI Plan
              </Typography>
              {canReadAutoTagging && canExecuteAutoTagging ? (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={autoTaggingEnabled}
                      onChange={handleAutoTaggingSwitchChange}
                      disabled={autoTaggingStatusLoading || savingAutoTagging}
                      color="primary"
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.875rem", color: "#374151" }}>
                      Auto-tagging
                    </Typography>
                  }
                  sx={{ ml: 0 }}
                />
              ) : null}
            </Box>
            <Typography sx={pageSectionSubtitleSx}>
              Define assessment logic and values to ensure consistent AI
              evaluation and accurate downstream process.
            </Typography>
          </Box>
        </Box>

        {!showAccessDenied ? (
          <Box sx={regulationDetailsSectionTabStripSx}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons={false}
              sx={regulationDetailsSectionTabsSx}
            >
              {(Object.keys(AI_PLAN_TAB_LABELS) as AiPlanTabKey[]).map(
                (tabKey) => (
                  <Tab
                    key={tabKey}
                    value={tabKey}
                    label={AI_PLAN_TAB_LABELS[tabKey].line1}
                  />
                ),
              )}
            </Tabs>
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          bgcolor: "#f9fafb",
          pl: 0,
          py: "16px",
        }}
      >
        {showAccessDenied ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 320,
              gap: 1,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Access Denied
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You do not have permission to view Organisational AI Plan.
            </Typography>
          </Box>
        ) : (
          <>
            {tab === "taggingPlan" ? (
              <TaggingPlanTab key={acTenantId || "no-ac-tenant"} />
            ) : null}
            {tab === "enhancedAssessmentPlan" ? (
              <EnhancedTaggingRuleTab key={acTenantId || "no-ac-tenant"} />
            ) : null}
          </>
        )}
      </Box>

      <AutoTaggingConfirmDialog
        open={autoTaggingConfirmMode !== null}
        mode={autoTaggingConfirmMode}
        saving={savingAutoTagging}
        onClose={handleCloseAutoTaggingConfirm}
        onConfirm={handleConfirmAutoTagging}
      />
    </Box>
  );
}
